import React from 'react'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  IconInfoOff,
  IconVisibilityOff,
  Inline,
  Text,
  TextLinkButton,
  useDialogControllers
} from '@machobear/design-system'
import { useDispatch } from 'react-redux'

import ENV_VARS from '@/config'
import toBoolean from '@/utils/to-boolean'
import useGetUserFlag from '@/hooks/use-get-user-flag'
import { WarningIconTriangle } from '@/components/icons'
import { ListingExternalEditNoticeModal } from '@/components/listing-external-edit-notice-modal'

const { DASHBOARD_URL } = ENV_VARS
interface Props {
  listingId: string
  type: 'pending' | 'hidden' | 'draft'
  isImported?: boolean
  externalInfo?: {
    external_id: string
    external_url: string
    realtor_name: string
    source_info_abbreviation: string
    source_info_name: string
  }
}

const ListingMessageHeader = ({
  type,
  listingId,
  isImported,
  externalInfo
}: Props) => {
  const { visible, showModal, closeModal } = useDialogControllers({
    listingConfirmation: false,
    editListingOutsideModal: false
  })
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isHidden = type === 'hidden'
  const isDraft = type === 'draft'
  const messages = {
    pending: t(
      'listing:b.listing.preview.header.pending.message',
      'This listing is still pending verification and is only viewable to you.'
    ),
    hidden: t(
      'listing:b.listing.preview.header.hidden.message',
      'This listing is hidden and is only viewable to you.'
    ),
    draft: t(
      'listing:b.listing.preview.header.draft.message',
      'This listing is set to draft and is only viewable to you.'
    )
  }
  const message = messages[type]

  const handleClick = () =>
    window.open(`${DASHBOARD_URL}/listing/edit/${listingId}`, '_blank')

  const unhideListing = async () => {
    await dispatch.listing.updateListing({
      listing_id: listingId,
      is_hidden: 0
    })

    closeModal('listingConfirmation')()
  }

  const hideExternalListingNoticeFlag = useGetUserFlag(
    'skip_edit_your_listing_on_trreb_modal'
  )
  const hideExternalListingNotice = toBoolean(
    hideExternalListingNoticeFlag?.key_value
  )

  const handleUnhide = () => {
    if (isImported) {
      hideExternalListingNotice
        ? window.open(externalInfo?.external_url, '_blank')
        : showModal('editListingOutsideModal')()
    } else {
      showModal('listingConfirmation')()
    }
  }

  if (!message) return null

  return (
    <Box
      background="info"
      paddingTop="xsmall"
      paddingBottom="xsmall"
      paddingX="gutter"
      marginBottom={{ mobile: 'small', tablet: 'gutter' }}
      width="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        height: '50px'
      }}
    >
      <Inline align="center" space="xsmall" alignY="center">
        <Columns space="xsmall" alignY="center">
          <Column width="content">
            <IconInfoOff size="xsmall" />
          </Column>
          <Column>
            <Inline space="xxsmall">
              <Text align="left" weight="strong" component="span" size="small">
                {messages[type]}{' '}
                {isHidden && (
                  <TextLinkButton onClick={handleUnhide}>
                    {t(
                      'listing:b.listing.preview.header.unhide_listing.message',
                      'Unhide listing.'
                    )}
                  </TextLinkButton>
                )}
                {isDraft && (
                  <TextLinkButton onClick={handleClick}>
                    {t(
                      'listing:b.listing.preview.header.finish_listing.message',
                      'Finish your listing.'
                    )}
                  </TextLinkButton>
                )}
              </Text>
            </Inline>
          </Column>
        </Columns>
      </Inline>
      <Dialog
        id="unhide-listing-confirmation"
        illustration={
          <Box marginTop="gutter" marginBottom="xxsmall">
            <WarningIconTriangle width={60} height={60} />
          </Box>
        }
        title="This listing is hidden from public search."
        description={
          <Box marginTop="small">
            <Text tone="secondary" align="center" size="medium">
              {t(
                'listing:b.listing.preview.header.modal.description',
                'Do you want to unhide this listing?'
              )}
            </Text>
          </Box>
        }
        open={visible.listingConfirmation}
        onClose={closeModal('listingConfirmation')}
        width="xsmall"
      >
        <Columns space="gutter">
          <Column width="1/2">
            <Button
              tone="neutral"
              variant="transparent"
              onClick={closeModal('listingConfirmation')}
            >
              Cancel
            </Button>
          </Column>
          <Column>
            <Button
              variant="solid"
              onClick={unhideListing}
              icon={<IconVisibilityOff />}
            >
              Unhide
            </Button>
          </Column>
        </Columns>
      </Dialog>
      <ListingExternalEditNoticeModal
        visible={visible.editListingOutsideModal}
        closeModal={closeModal('editListingOutsideModal')}
        externalInfo={externalInfo}
      />
    </Box>
  )
}

export default ListingMessageHeader
